import type { TokenCacheStrategy, AuthToken, DafAuthConfig } from '../types';

interface CacheEntry {
    parentTokenValue?: string;
    token: AuthToken<DafAuthConfig>;
}

export class DafInMemoryCacheStrategy implements TokenCacheStrategy<DafAuthConfig> {
    /**
     * @param minTokenLifespan To be considered fresh, a token must not expire before this number of ms passes (defaults to 10 secs)
     */
    constructor(protected minTokenLifespan: number = 10000) {}

    protected cache: CacheEntry[] = [];

    /**
     * Add a token to the cache
     *
     * Each cache entry contains the token and a reference to it's parent token value if available
     * This ensures we can identify all child tokens associated with a token and purge them when purging the parent
     */
    public async addToken(token: AuthToken<DafAuthConfig>): Promise<void> {
        // Note we search by config rather than value here as the purpose of the addToken operation may be to update the tokenValue
        const cacheEntry = this.findTokenByConfig(token.config);
        if (cacheEntry) {
            /**
             * If a token with matching config is already cached and its value differs then purge it.
             * We want the fresh token to be returned from cache next time we ask for a token matching this config
             */
            if (cacheEntry.token.tokenValue !== token.tokenValue) {
                this.removeCacheEntry(cacheEntry);
            } else {
                /**
                 * If cached token exists and value matches, then update all other metadata associated with the token.
                 * Perhaps its lifespan/expiry has been extended...
                 */
                cacheEntry.token = {
                    ...token
                };
                return;
            }
        }
        let parentTokenValue: string;
        // Link the token to its parent token so we can purge it when the parent is purged from cache
        if (token.parentId) {
            const parentCacheEntry = this.cache.find((ce) => ce.token.id === token.parentId);
            parentTokenValue = parentCacheEntry && parentCacheEntry.token.tokenValue;
        }
        this.cache.push({
            token,
            parentTokenValue
        });
    }

    public async getToken(config: DafAuthConfig): Promise<AuthToken<DafAuthConfig>> {
        const cacheEntry: CacheEntry = this.findTokenByConfig(config);
        if (cacheEntry) {
            if (!cacheEntry.token.expires || cacheEntry.token.expires >= Date.now() + this.minTokenLifespan) {
                return cacheEntry.token;
            }
            this.removeCacheEntry(cacheEntry);
        }
        return undefined;
    }

    /**
     * Remove token and all of its child tokens from the cache
     */
    public async removeToken(token: AuthToken<DafAuthConfig>): Promise<void> {
        const cacheEntry = this.findTokenByValue(token.tokenValue);
        if (cacheEntry) {
            this.removeCacheEntry(cacheEntry);
        }
    }

    /**
     * Find a token in the cache based on the token config
     */
    protected findTokenByConfig(config: DafAuthConfig): CacheEntry {
        return this.cache.find(
            (ce) =>
                // app ID matches
                (!config.appMetaData ||
                    !config.appMetaData.id ||
                    (ce.token.config.appMetaData && ce.token.config.appMetaData.id === config.appMetaData.id)) &&
                // Environment matches
                config.environment === ce.token.config.environment &&
                // context matches
                config.context === ce.token.config.context &&
                // tokenSource matches
                config.tokenSource === ce.token.config.tokenSource &&
                // tokenType matches
                config.tokenType === ce.token.config.tokenType &&
                // grantType matches
                config.grantType === ce.token.config.grantType &&
                // isSeed matches
                config.isSeed === ce.token.config.isSeed
        );
    }

    /**
     * Find a token in the cache based on the token value
     */
    protected findTokenByValue(tokenValue: string): CacheEntry {
        return this.cache.find((ce) => ce.token.tokenValue === tokenValue);
    }

    /**
     * Purge a token from the cache and all associated child tokens
     */
    protected removeCacheEntry(cacheEntry: CacheEntry, skipRemovingChildren: boolean = false) {
        this.cache = this.cache.filter(
            (ce) =>
                // Purge the supplied cache entry itself
                ce !== cacheEntry &&
                // Purge all cache entries which are children of the supplied cache entry
                (skipRemovingChildren || !ce.parentTokenValue || ce.parentTokenValue !== cacheEntry.token.tokenValue)
        );
    }
}
